import type { IUsageFeeBill } from '../../types';

import { sortByCycleStartDate } from '../../helpers';
import { useUsageFeeBillStore } from '../../stores';
import { dayjs } from '~/utils/dayjs';

const BILL_FAILED_THRESHOLD_DAYS = 30;

interface IFailedUsageFeeBillData {
  failedBills?: IUsageFeeBill[];
  firstFailedBill?: IUsageFeeBill;
  daysSinceEarliestFailure: number;
  thresholdDate: string;
  totalFailedBills: number;
  totalFailedAmount: number;
  hasBillFailed: boolean;
  hasBillFailedAfterThresholdDay: boolean;
  hasBillFailedBeforeThresholdDay: boolean;
}

export const usePricingUsageFeeBillFailed = (): IFailedUsageFeeBillData => {
  const usageFeeBillFails = useUsageFeeBillStore((state) => state.usageFeeBillFails);

  const failedBills = sortByCycleStartDate(usageFeeBillFails);
  const totalFailedBills = failedBills?.length ?? 0;
  const firstFailedBill = getEarliestFailedBill(failedBills ?? []);
  const daysSinceEarliestFailure = calculateDaysSinceFailure(firstFailedBill?.cycleEndDate);
  const totalFailedAmount = calculateTotalFailedAmount(failedBills ?? []);
  const thresholdDate = calculateThresholdDate(firstFailedBill?.cycleEndDate);

  const hasBillFailed = totalFailedBills > 0;
  const hasBillFailedAfterThresholdDay = daysSinceEarliestFailure > BILL_FAILED_THRESHOLD_DAYS;
  const hasBillFailedBeforeThresholdDay =
    hasBillFailed && daysSinceEarliestFailure <= BILL_FAILED_THRESHOLD_DAYS;

  return {
    failedBills,
    totalFailedBills,
    firstFailedBill,
    daysSinceEarliestFailure,
    hasBillFailed,
    hasBillFailedAfterThresholdDay,
    totalFailedAmount,
    thresholdDate,
    hasBillFailedBeforeThresholdDay,
  };
};

function calculateDaysSinceFailure(cycleEndDate?: string): number {
  if (!cycleEndDate) return 0;
  return Math.ceil(dayjs.utc().diff(dayjs.utc(cycleEndDate), 'day', true));
}

function getEarliestFailedBill(bills: IUsageFeeBill[]): IUsageFeeBill | undefined {
  const sortedBills = sortByCycleStartDate(bills);
  if (!sortedBills) return undefined;

  return sortedBills[0];
}

function calculateThresholdDate(cycleEndDate?: string): string {
  if (!cycleEndDate) return '';
  return dayjs(cycleEndDate).add(BILL_FAILED_THRESHOLD_DAYS, 'day').toString();
}

function calculateTotalFailedAmount(bills: IUsageFeeBill[]): number {
  const totalFailedAmount = bills?.reduce((sum, bill) => sum + (Number(bill.price) ?? 0), 0) ?? 0;
  return Number(totalFailedAmount.toFixed(2));
}
